import React, { useState, useEffect } from 'react';
import { Table, Button, Select, Card, Space, notification } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { getYears } from '../util/APIUtils';
import { API_BASE_URL, ACCESS_TOKEN } from '../constants';

const { Option } = Select;

const ThirdPartyOrders = () => {
    const [years, setYears] = useState([]);
    const [selectedYear, setSelectedYear] = useState(null);
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        loadYears();
    }, []);

    useEffect(() => {
        if (selectedYear) {
            loadData(selectedYear);
        }
    }, [selectedYear]);

    const loadYears = () => {
        getYears()
            .then(response => {
                // Sort years in descending order
                const sortedYears = response.sort((a, b) => b.projectYear - a.projectYear);
                setYears(sortedYears);
                if (sortedYears.length > 0) {
                    // Set default year to the most recent one or current year logic
                    const currentYear = new Date().getFullYear();
                    const foundYear = sortedYears.find(y => y.projectYear === currentYear);
                    if (foundYear) {
                        setSelectedYear(foundYear.projectYear);
                    } else {
                        setSelectedYear(sortedYears[0].projectYear);
                    }
                }
            })
            .catch(error => {
                notification.error({
                    message: 'Kultursaat',
                    description: 'Jahre konnten nicht geladen werden.'
                });
            });
    };

    const loadData = (year) => {
        setLoading(true);
        fetch(`${API_BASE_URL}/reports/third-party-orders/${year}`, {
            headers: {
                'Authorization': 'Bearer ' + localStorage.getItem(ACCESS_TOKEN)
            }
        })
            .then(response => {
                if (!response.ok) {
                    throw new Error('Network response was not ok');
                }
                return response.json();
            })
            .then(jsonData => {
                setData(jsonData);
                setLoading(false);
            })
            .catch(error => {
                setLoading(false);
                notification.error({
                    message: 'Kultursaat',
                    description: 'Daten konnten nicht geladen werden.'
                });
            });
    };

    const handleYearChange = (value) => {
        setSelectedYear(value);
    };


    const handleDownload = () => {
        if (!selectedYear) return;
        
        fetch(`${API_BASE_URL}/reports/third-party-orders/download/${selectedYear}`, {
            headers: {
                'Authorization': 'Bearer ' + localStorage.getItem(ACCESS_TOKEN)
            }
        })
        .then(response => {
             if (!response.ok) {
                throw new Error('Network response was not ok');
            }
            return response.blob();
        })
        .then(blob => {
            const url = window.URL.createObjectURL(new Blob([blob]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Auftraege_an_Dritte_${selectedYear}.xls`);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
        })
        .catch(error => {
            notification.error({
                message: 'Kultursaat',
                description: 'Download fehlgeschlagen.'
            });
        });
    };

    const columns = [
        {
            title: 'Projektnummer',
            dataIndex: 'projectNumber',
            key: 'projectNumber',
        },
        {
            title: 'Projekttitel',
            dataIndex: 'projectTitle',
            key: 'projectTitle',
        },
        {
            title: 'Züchter',
            dataIndex: 'breederName',
            key: 'breederName',
        },
        {
            title: 'Kulturart',
            dataIndex: 'cropName',
            key: 'cropName',
        },
        {
            title: 'Auftragstitel',
            dataIndex: 'orderTitle',
            key: 'orderTitle',
        },
        {
            title: 'Menge',
            dataIndex: 'amount',
            key: 'amount',
            align: 'right',
            render: (text) => text ? parseFloat(text).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : ''
        },
        {
            title: 'Einzelpreis',
            dataIndex: 'pricePerUnit',
            key: 'pricePerUnit',
            align: 'right',
            render: (text) => text ? parseFloat(text).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' }) : ''
        },
        {
            title: 'Gesamt',
            dataIndex: 'totalPrice',
            key: 'totalPrice',
            align: 'right',
            render: (text) => text ? parseFloat(text).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' }) : ''
        },
    ];

    // Calculate total sum
    const totalSum = data.reduce((sum, item) => sum + (item.totalPrice || 0), 0);

    return (
        <div style={{ padding: '24px' }}>
            <Card title="Aufträge an Dritte">
                <Space style={{ marginBottom: 16 }}>
                    <Select 
                        value={selectedYear} 
                        onChange={handleYearChange} 
                        style={{ width: 120 }}
                        placeholder="Jahr wählen"
                    >
                        {years.map(year => (
                            <Option key={year.id} value={year.projectYear}>{year.projectYear}</Option>
                        ))}
                    </Select>
                    <Button 
                        type="primary" 
                        icon={<DownloadOutlined />} 
                        onClick={handleDownload}
                        disabled={!selectedYear}
                    >
                        Excel herunterladen
                    </Button>
                </Space>
                
                <Table 
                    columns={columns} 
                    dataSource={data} 
                    rowKey={(record, index) => index} 
                    loading={loading}
                    pagination={false}
                    summary={() => (
                        <Table.Summary fixed>
                            <Table.Summary.Row style={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
                                <Table.Summary.Cell index={0} colSpan={7} style={{ textAlign: 'right' }}>
                                    Gesamtsumme
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={1} style={{ textAlign: 'right' }}>
                                    {totalSum.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
                                </Table.Summary.Cell>
                            </Table.Summary.Row>
                        </Table.Summary>
                    )}
                />
            </Card>
        </div>
    );
};

export default ThirdPartyOrders;
